/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable class-methods-use-this */
import {
  Cell,
  CellTemplate,
  Compatible,
  getCellProperty,
  Uncertain,
  isAlphaNumericKey,
  keyCodes,
  UncertainCompatible,
  isNavigationKey,
} from '@silevis/reactgrid';
import { Flex, Menu, Text, MenuList, MenuButton, Box } from '@chakra-ui/react';
import { BiFilter } from 'react-icons/bi';
import { ComponentProps, useState } from 'react';

import { DriverPanelBarsFilterIcon } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons/DriverPanelFigmaIcons';
import { usePlanRevampTable } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/PlanRevampTableContext';

import FiltersCheckBox from './Filters';

// Wraps MenuList with the modern (Dimensions "Items" table) filter popup
// look, keeping the styling in one place instead of five separate
// `isModernFilter ? value : undefined` props on the shared MenuList below.
const ModernMenuList = (properties: ComponentProps<typeof MenuList>) => (
  <MenuList
    border='1px solid #E8E9EB'
    borderRadius='14px'
    boxShadow='0px 8px 24px rgba(18, 24, 38, 0.12)'
    p={0}
    minWidth='unset'
    {...properties}
  />
);

export interface SelectCell extends Cell {
  type: 'select';
  text: string;
  placeholder?: string;
  hasEdit: boolean;
  hasDelete: boolean;
}

const FiltersMenu = ({ cell, filterChanges }: any) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isRevampTable = usePlanRevampTable();
  // Scoped to the Dimensions "Items" table only (set on the cell data by
  // NewTableHelpers.tsx / TableHelpers.tsx / TableHelpersopz.tsx) — lets this
  // shared component (also used by 3 Plan-page tables) render a modern
  // purple-themed filter popup for just that table without touching the
  // `variant={'filter'}` menu theme everyone else still relies on.
  const isModernFilter = Boolean(cell?.isModernFilter);
  const FilterMenuList = isModernFilter ? ModernMenuList : MenuList;

  const handleFilterChanges = (data: any) => {
    filterChanges(data);
  };

  const handleCancelFilter = () => {
    setIsMenuOpen(false);
  };

  return (
    <Flex
      width='100%'
      height='100%'
      alignItems='center'
      justifyContent='space-between'
      gap={isRevampTable ? '10px' : undefined}
    >
      <Text
        fontFamily={isRevampTable ? 'bold' : undefined}
        fontSize={isRevampTable ? '14px' : undefined}
        fontWeight={isRevampTable ? 700 : undefined}
        color={isRevampTable ? '#121826' : undefined}
        noOfLines={1}
        flex='1'
      >
        {cell.text}
      </Text>

      <Menu
        variant={isModernFilter ? undefined : 'filter'}
        placement='bottom-end'
        strategy='fixed'
        isOpen={isMenuOpen}
        onOpen={() => {
          setIsMenuOpen(true);
        }}
        onClose={() => setIsMenuOpen(false)}
      >
        <>
          <MenuButton onClick={(event: any) => event?.stopPropagation()}>
            <Box display='flex' alignItems='center'>
              {isRevampTable ? (
                <DriverPanelBarsFilterIcon />
              ) : (
                <span className='rg-legacy-filter-icon'>
                  <BiFilter size={30} color='rgb(190 218 218)' />
                </span>
              )}
            </Box>
          </MenuButton>

          <FilterMenuList
            onCopy={(event) => event.stopPropagation()}
            onCut={(event) => event.stopPropagation()}
            onPaste={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (isAlphaNumericKey(event.keyCode) || isNavigationKey(event.keyCode))
                event.stopPropagation();
            }}
          >
            <FiltersCheckBox
              checkboxData={cell}
              handleFilterChanges={handleFilterChanges}
              cancelFilter={handleCancelFilter}
              isModernFilter={isModernFilter}
            />
          </FilterMenuList>
        </>
      </Menu>
    </Flex>
  );
};

const RevampFilterEditInput = ({
  cell,
  onCellChanged,
  getCompatibleCell,
}: {
  cell: Compatible<SelectCell>;
  onCellChanged: (cell: Compatible<SelectCell>, commit: boolean) => void;
  getCompatibleCell: (cell: Uncertain<SelectCell>) => Compatible<SelectCell>;
}) => {
  const isRevampTable = usePlanRevampTable();

  return (
    <input
      disabled={cell.nonEditable}
      style={{
        backgroundColor: isRevampTable ? '#F5F0FF' : '#423c82',
        color: isRevampTable ? '#121826' : 'white',
        border: isRevampTable ? '1px solid #C4B5FD' : undefined,
        fontFamily: isRevampTable ? 'bold' : undefined,
        fontSize: isRevampTable ? '14px' : undefined,
        fontWeight: isRevampTable ? 700 : undefined,
        padding: isRevampTable ? '0 12px' : undefined,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
      }}
      ref={(input) => {
        if (input) {
          input.focus();
          input.setSelectionRange(input?.value?.length, input?.value?.length);
        }
      }}
      defaultValue={cell.text}
      onChange={(event) =>
        onCellChanged(getCompatibleCell({ ...cell, text: event.currentTarget.value }), false)
      }
      onBlur={(event) =>
        onCellChanged(
          getCompatibleCell({ ...cell, text: event.currentTarget.value }),
          (event as any).view?.event?.keyCode !== keyCodes.ESCAPE,
        )
      }
      onCopy={(event) => event.stopPropagation()}
      onCut={(event) => event.stopPropagation()}
      onPaste={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      placeholder={cell.placeholder}
      onKeyDown={(event) => {
        if (isAlphaNumericKey(event.keyCode) || isNavigationKey(event.keyCode))
          event.stopPropagation();
      }}
    />
  );
};

export class FiltersCellTemplate implements CellTemplate<Cell | SelectCell> {
  onFilterChange: (data: any, cell: any) => void;

  constructor(onFilterChange: (data: any, cell: any) => void) {
    this.onFilterChange = onFilterChange;
  }

  getCompatibleCell(uncertainCell: Uncertain<SelectCell>): Compatible<SelectCell> {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    const hasEdit = getCellProperty(uncertainCell, 'hasEdit', 'boolean');
    const hasDelete = getCellProperty(uncertainCell, 'hasDelete', 'boolean');
    const value = Number.parseFloat(text);
    return { ...uncertainCell, text, value, hasEdit, hasDelete };
  }

  render(
    cell: Compatible<SelectCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<SelectCell>, commit: boolean) => void,
  ): JSX.Element {
    return (
      <>
        {isInEditMode && cell.hasEdit ? (
          <RevampFilterEditInput
            cell={cell}
            onCellChanged={onCellChanged}
            getCompatibleCell={this.getCompatibleCell.bind(this)}
          />
        ) : (
          <FiltersMenu cell={cell} filterChanges={(data: any) => this.onFilterChange(cell, data)} />
        )}
      </>
    );
  }

  handleKeyDown(
    cell: Compatible<SelectCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<SelectCell>; enableEditMode: boolean } {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) return { cell, enableEditMode: true };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  update(
    cell: Compatible<SelectCell>,
    cellToMerge: UncertainCompatible<SelectCell>,
  ): Compatible<SelectCell> {
    return this.getCompatibleCell({ ...cell, text: cellToMerge.text });
  }
}
